/**
 * Centralized Design Token System
 *
 * Single source of truth for all spacing, sizing, and layout tokens
 * used across renderers. Eliminates duplication of token maps
 * in element-renderer, badge-renderer, container-renderer, card-renderer, etc.
 */

/**
 * Semantic spacing tokens → Tailwind spacing scale values.
 * Used for gap, padding, margin, and general spacing properties.
 *
 * Mapping: xs=1 (4px), sm=2 (8px), md=4 (16px), lg=6 (24px), xl=8 (32px)
 */
const SPACING_SCALE: Record<string, string> = {
  xs: "1",
  sm: "2",
  md: "4",
  lg: "6",
  xl: "8",
};

/**
 * Position offset tokens → Tailwind spacing scale values.
 * Used for absolute/fixed position offsets (top, right, bottom, left).
 * Slightly larger than SPACING_SCALE for visual clarity of positioned elements.
 *
 * Mapping: xs=1.5 (6px), sm=3 (12px), md=4 (16px), lg=6 (24px), xl=8 (32px)
 */
const POSITION_SCALE: Record<string, string> = {
  xs: "1.5",
  sm: "3",
  md: "4",
  lg: "6",
  xl: "8",
};

/**
 * Resolve a spacing token (xs/sm/md/lg/xl) or raw value to a Tailwind spacing class value.
 * Used for gap, padding, margin.
 *
 * @example spacingToken("md") → "4"
 * @example spacingToken("16") → "16"
 * @example spacingToken(4) → "4"
 */
export function spacingToken(token: string | number): string {
  if (typeof token === "number") return String(token);
  if (/^\d+(\.\d+)?$/.test(token)) return token;
  // Convert rem values to Tailwind spacing scale (1rem = 4 units)
  const remMatch = /^(\d+(?:\.\d+)?)rem$/.exec(String(token));
  if (remMatch) {
    const remVal = parseFloat(remMatch[1]);
    const twVal = remVal * 4;
    // Use integer if whole number, otherwise keep decimal
    return twVal % 1 === 0 ? String(twVal) : String(twVal);
  }
  return SPACING_SCALE[token] ?? token;
}

/**
 * Convert a source-projected pixel measurement to the equivalent Tailwind
 * spacing scale value. Object-shaped padding from the source projection uses
 * real pixels (`{ top: 32, ... }`), unlike scalar Cogo spacing tokens where
 * `8` already means Tailwind's spacing token 8.
 */
export function pixelSpacingToken(px: number): string {
  if (px <= 0) return "0";
  if (px >= 64) return "16";
  if (px >= 56) return "14";
  if (px >= 48) return "12";
  if (px >= 40) return "10";
  if (px >= 32) return "8";
  if (px >= 24) return "6";
  if (px >= 20) return "5";
  if (px >= 16) return "4";
  if (px >= 12) return "3";
  if (px >= 8) return "2";
  if (px >= 4) return "1";
  return "0.5";
}

/** Resolve scalar token padding or source-projected directional pixel padding. */
export function paddingClasses(
  padding: string | number | Record<string, unknown>,
): string {
  if (typeof padding === "number") return `p-${spacingToken(padding)}`;

  if (typeof padding === "object" && padding !== null) {
    const parts: string[] = [];
    const side = (value: unknown): string | null => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return pixelSpacingToken(value);
      }
      if (typeof value === "string" && value.trim()) {
        return spacingToken(value);
      }
      return null;
    };
    for (const [key, prefix] of [
      ["top", "pt"],
      ["right", "pr"],
      ["bottom", "pb"],
      ["left", "pl"],
    ] as const) {
      const token = side(padding[key]);
      if (token) parts.push(`${prefix}-${token}`);
    }
    return parts.join(" ");
  }

  const splitParts = padding.trim().split(/\s+/);
  if (splitParts.length === 1) return `p-${spacingToken(splitParts[0])}`;
  if (splitParts.length === 2) {
    return `py-${spacingToken(splitParts[0])} px-${spacingToken(splitParts[1])}`;
  }
  if (splitParts.length === 4) {
    return `pt-${spacingToken(splitParts[0])} pr-${spacingToken(splitParts[1])} pb-${spacingToken(splitParts[2])} pl-${spacingToken(splitParts[3])}`;
  }
  return `p-${spacingToken(splitParts[0])}`;
}

/**
 * Resolve a position offset token to a Tailwind spacing class value.
 * Used for top/right/bottom/left in absolute/fixed positioning.
 *
 * @example positionToken("sm") → "3"
 * @example positionToken("1.5") → "1.5"
 * @example positionToken("-1") → "-1"
 */
export function positionToken(token: string): string {
  return POSITION_SCALE[token] ?? token;
}
